import { z } from "zod";
import RuntimeConfigError from "_shared/runtime-config/runtime-config-error";

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema.type>;

const baseUrl = z.url({ protocol: /^https?$/ }).transform((url) => url.replace(/\/+$/, ""));

/**
 * The shape of `/config.json`: everything that differs between the environments one bundle is
 * deployed to (#7516). The deploy writes it beside the bundle (`_deploy-frontend.yml`), and the Vite
 * dev and preview servers answer it locally (`app/scripts/runtime-config.ts`).
 *
 * Strict, so a misspelled key is refused rather than read as absent. A name nothing reads is how the
 * Places key shipped missing for three weeks (#4983).
 *
 * This does not go through `parseOrThrow`: that reports failures through the client error reporter,
 * which reads the API address this file is what supplies.
 */
export default class RuntimeConfigSchema {
  public static readonly type = z.strictObject({
    /** The Sentry environment, and `development` for the dev server, which `IS_DEV` keys off. */
    environment: z.string().trim().min(1),
    apiBaseUrl: baseUrl,
    siteBaseUrl: baseUrl,
    /** The Ably channel prefix: `pr-<n>` on a preview environment, absent everywhere else. */
    ablyChannelNamespace: z.string().trim().optional(),
    /** A browser key, not a secret. Empty locally, where every address field falls back to manual entry. */
    googlePlacesApiKey: z.string().trim(),
  });

  public static validate(values: unknown, source: string): RuntimeConfig {
    const parsed = RuntimeConfigSchema.type.safeParse(values);
    if (parsed.success) return parsed.data;

    throw new RuntimeConfigError(`${source} is not a valid configuration:\n${z.prettifyError(parsed.error)}`, {
      cause: parsed.error,
    });
  }
}
